import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { MaImageUsage } from '@/lib/zybooks-api';
import { AlertTriangle } from 'lucide-react';

export function formatMaImageUsageShortLabel(usage: MaImageUsage): string {
    if (usage.zybook_code && usage.chapter_number != null && usage.section_number != null) {
        return `${usage.zybook_code} Ch ${usage.chapter_number}.${usage.section_number}`;
    }

    return usage.document_name;
}

export function buildMaImageUsageHref(usage: MaImageUsage): string {
    if (usage.zybook_code) {
        return `/books/${encodeURIComponent(usage.zybook_code)}/${usage.ma_document_id}`;
    }

    return `/documents/${usage.ma_document_id}`;
}

export function filterMaImageUsagesForEditor(usages: MaImageUsage[], currentMaDocumentId?: number | null): MaImageUsage[] {
    if (typeof currentMaDocumentId !== 'number' || currentMaDocumentId <= 0) {
        return usages;
    }

    return usages.filter(usage => usage.ma_document_id !== currentMaDocumentId);
}

function getMaImageUsageKey(usage: MaImageUsage): string {
    return `${usage.ma_document_id}-${usage.zybook_code ?? 'none'}-${usage.chapter_number ?? 'x'}-${usage.section_number ?? 'x'}`;
}

interface MaImageUsageBadgeProps {
    usages: MaImageUsage[];
    loading?: boolean;
    error?: string | null;
    currentMaDocumentId?: number | null;
}

export function MaImageUsageBadge({ usages, loading, error, currentMaDocumentId }: MaImageUsageBadgeProps) {
    if (loading || error) {
        return null;
    }

    const otherUsages = filterMaImageUsagesForEditor(usages, currentMaDocumentId);
    if (otherUsages.length === 0) {
        return null;
    }

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Badge
                        variant="secondary"
                        className="inline-flex shrink-0 items-center gap-1 text-xs"
                    >
                        <AlertTriangle className="h-3 w-3" />
                        Shared
                    </Badge>
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    className="max-w-xs"
                >
                    <p className="mb-2 text-sm">Saving updates shared library alt text everywhere this image is used.</p>
                    <ul className="space-y-1 text-sm">
                        {otherUsages.map(usage => (
                            <li key={getMaImageUsageKey(usage)}>
                                <a
                                    href={buildMaImageUsageHref(usage)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                >
                                    {formatMaImageUsageShortLabel(usage)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
